import type { ClientDetail, UpdateClientPayload } from '@/api/clients';

/** Локальное состояние формы редактирования "Общее" — из ClientDetail в момент входа в режим редактирования. */
export interface GeneralEditState {
  name: string;
  status: string;
  type: string;
  tariffType: string;
  /** '' = бессрочно (null на бэке). */
  validity: string;
  isMt1HallAllowed: boolean;
  notes: string;
  operatorTemplateId: string;
  ogrn: string;
  address: string;
  lastName: string;
  firstName: string;
  middleName: string;
  phone: string;
  position: string;
  tutorIds: number[];
}

export function buildEditState(data: ClientDetail): GeneralEditState {
  return {
    name: data.company ?? '',
    status: data.status,
    type: data.type ?? 'external',
    tariffType: data.tariffType,
    validity: data.validity ?? '',
    isMt1HallAllowed: data.isMt1HallAllowed,
    notes: data.notes ?? '',
    operatorTemplateId: data.personalData.operatorTemplateId ? String(data.personalData.operatorTemplateId) : '',
    ogrn: data.personalData.ogrn ?? '',
    address: data.personalData.address ?? '',
    lastName: data.adminContact.lastName ?? '',
    firstName: data.adminContact.firstName ?? '',
    middleName: data.adminContact.middleName ?? '',
    phone: data.adminContact.phone ?? '',
    position: data.adminContact.position ?? '',
    tutorIds: data.managers.map((m) => m.id),
  };
}

export function buildUpdatePayload(state: GeneralEditState): UpdateClientPayload {
  return {
    name: state.name,
    status: state.status,
    type: state.type,
    tariffType: state.tariffType,
    validity: state.validity || null,
    isMt1HallAllowed: state.isMt1HallAllowed,
    notes: state.notes || null,
    personalData: {
      operatorTemplateId: state.operatorTemplateId ? Number(state.operatorTemplateId) : null,
      ogrn: state.ogrn || null,
      address: state.address || null,
    },
    adminContact: {
      lastName: state.lastName || null,
      firstName: state.firstName || null,
      middleName: state.middleName || null,
      phone: state.phone || null,
      position: state.position || null,
    },
    tutorIds: state.tutorIds,
  };
}

/** Читаемые названия обязательных полей — для сообщения об ошибке и подсветки конкретных полей. */
export const REQUIRED_FIELD_LABELS: Record<string, string> = {
  name: 'Наименование компании',
  tutorIds: 'Кураторы',
  ogrn: 'ОГРН',
  address: 'Юридический адрес',
};

/**
 * Обязательные поля формы "Общее" — Тип/Статус/Тип тарификации всегда имеют
 * значение (это селекты, у них нет пустого состояния), реально можно
 * оставить пустыми только текстовые/мультиселект поля. ОГРН/Юридический
 * адрес обязательны только при статусе не "demo" — 1:1 Orchid
 * (CustomerPersonalDataLayout, см. докблок GeneralEditForm). "Шаблон
 * соглашения" помечен звёздочкой визуально (как в Orchid), но "По умолчанию"
 * (пустое значение) — валидный явный выбор, а не "не заполнено", поэтому
 * блокировать сохранение этим полем не нужно.
 */
export function validateEditState(state: GeneralEditState): string[] {
  const missing: string[] = [];
  if (!state.name.trim()) missing.push('name');
  if (state.tutorIds.length === 0) missing.push('tutorIds');
  if (state.status !== 'demo') {
    if (!state.ogrn.trim()) missing.push('ogrn');
    if (!state.address.trim()) missing.push('address');
  }
  return missing;
}
